import type { ControlPlaneIssueId } from "@obsina/domain";
import { APP_TIME_ZONE_LABEL } from "./locale";
import type {
  CoverageDurabilityKey,
  MemoryGraphMessages,
} from "./memory-graph/types";

/**
 * The one lead sentence a `partial` answer opens with, keyed by whether the gap
 * can still move. Shared by the commitment list and the memory graph so the two
 * surfaces never describe the same coverage differently.
 *
 * Every line states what is missing without promising it fills in: `terminal`
 * says so outright, and even `transient` says "still being read" rather than
 * "not yet complete", which reads as a deadline nobody set. The word "partly"
 * carries the state in text, never colour alone.
 */
const COVERAGE_PARTIAL_LEAD = {
  transient: "Kısmen okundu — kalanı okunuyor",
  terminal: "Kısmen okundu — kalanı kendiliğinden okunmayacak",
  mixed: "Kısmen okundu — bir kısmı okunuyor, kalanı kendiliğinden okunmayacak",
  // A partial answer always carries at least one reason, so neither of these is
  // reachable from a self-consistent envelope. They state the fact and claim
  // nothing about waiting rather than inventing a durability.
  settled: "Kısmen okundu",
  unstated: "Kısmen okundu",
} as const satisfies Record<CoverageDurabilityKey, string>;

/**
 * The same statement as {@link COVERAGE_PARTIAL_LEAD}, cut to what fits on one
 * line of the map's bottom strip.
 *
 * The strip carries the as-of, the bound on recall and the authorization note
 * side by side, and the lead alone — with the derived counts and every gap
 * sentence appended after it — ran to three lines under the live tenant's map.
 * A reader glancing at a strip is asking one question, "is this everything",
 * and that is the clause kept here. The rest is one click away, whole.
 */
const COVERAGE_PARTIAL_SUMMARY = {
  // Only the durability a reader can still act on earns the second clause: a
  // gap that is still closing is worth coming back for, and the strip has room
  // for that and nothing more. The terminal case says the bound and stops; what
  // it means -- that this is as far as it will be read -- is the detail's first
  // line, one click away and stated in full.
  transient: "Sınırlı görünüm · okuma sürüyor",
  mixed: "Sınırlı görünüm · okuma sürüyor",
  terminal: "Sınırlı görünüm",
  settled: "Sınırlı görünüm",
  unstated: "Sınırlı görünüm",
} as const satisfies Record<CoverageDurabilityKey, string>;

/**
 * Why an `unknown` answer is unknown, keyed by whether waiting can still change
 * it. `withheld` names the thing not being presented ("list", "map").
 *
 * The wording used to open with "Required coverage is not yet complete" in every
 * case. On a terminal gap — a revoked connection, a source nobody can map — "not
 * yet" is a promise the pipeline cannot keep: nothing will change until a person
 * acts, and the reason list below is where that action is named. Composed from
 * one template so the commitment and graph surfaces cannot drift apart.
 */
function unknownCoverageBody(
  withheld: string,
): Record<CoverageDurabilityKey, string> {
  const tail = `bu yüzden Obsina eksik bir ${withheld} kesin sonuç gibi sunmak yerine bunu bilinmiyor olarak bildirir. Bu bir uyarı değildir.`;
  return {
    transient: `Gerekli kapsam okunuyor, ${tail}`,
    terminal: `Gerekli kapsam eksik ve kendiliğinden kapanmayacak, ${tail}`,
    mixed: `Gerekli kapsamın bir kısmı okunuyor, kalanı kendiliğinden kapanmayacak, ${tail}`,
    settled: `Gerekli kapsam eksik, ${tail}`,
    unstated: `Gerekli kapsam eksik, ${tail}`,
  };
}

export const enMessages = {
  productArea: "Yönetim düzlemi",
  sourceArea: "Microsoft 365",
  oauthPopupCompleting: "Microsoft girişi tamamlanıyor…",
  oauthPopupClosing:
    "Microsoft girişi bitti. Pencere kendiliğinden kapanmazsa kapatabilirsiniz.",
  common: {
    actionRequired: "İşlem gerekiyor",
    loading: "Yönetim düzlemi yükleniyor",
    notAvailable: "Kullanılamıyor",
    notSelected: "Seçilmedi",
    skipToContent: "Ana içeriğe geç",
    updateComplete: "Güncelleme tamam",
  },
  auth: {
    eyebrow: "Giriş",
    title: "İş hesabınızla devam edin",
    body: "Obsina sizi kurumunuzun hesabıyla oturuma alır. Yönetilecek yeni bir parola yok.",
    providersGroupLabel: "Giriş sağlayıcıları",
    microsoftLabel: "Microsoft ile devam et",
    microsoftSub: "Microsoft 365 · Entra ID",
    microsoftConnecting: "Microsoft'a yönlendiriliyor…",
    microsoftSignInFailed: "Microsoft girişi başlatılamadı.",
    googleLabel: "Google ile devam et",
    googleSub: "Google Workspace",
    googleConnecting: "Google'a yönlendiriliyor…",
    googleSignInFailed: "Google girişi başlatılamadı.",
    connectingAnnouncement: (provider: string) =>
      `Giriş için ${provider} sağlayıcısına yönlendiriliyor.`,
    signInFailedTitle: "Giriş başarısız",
    privacyNote:
      "Obsina parolanızı hiç görmez. Giriş sağlayıcınızda yapılır; posta kutusu kimlik bilgileri sunucuda ve şifreli kalır, bu tarayıcıya hiç girmez.",
    companyMemory: "Şirket hafızası",
    secureUnavailableTitle: "Güvenli giriş kullanılamıyor",
    stagingConfigIncomplete: "Staging kimlik doğrulama yapılandırması eksik.",
    establishingSession: "Güvenli oturum kuruluyor…",
    sessionCouldNotEstablish: "Güvenli uygulama oturumu kurulamadı.",
    signOutClearFailed:
      "Çıkış, uygulama oturumunu güvenle temizleyemedi. Yeniden deneyin.",
    retrySecureSession: "Güvenli oturumu yeniden dene",
    signOut: "Çıkış yap",
    signingOut: "Çıkış yapılıyor…",
    organisationChoiceTitle: "Bir kurum seçin",
    organisationChoiceBody: "Hesabınız birden fazlasına ait.",
    organisationsGroupLabel: "Kuruluşlar",
    organisationsLoading: "Organisationlar yükleniyor…",
    organisationOpening: "Açılıyor…",
    organisationUnnamed: "Adsız kurum",
    organisationChoiceFailedTitle: "Kurum açılamadı",
    organisationsUnavailable: "Kurum listesi yüklenemedi.",
    organisationSelectFailed: "O kurum açılamadı. Yeniden deneyin.",
  },
  nav: {
    label: "Ana menü",
    controlPlane: "Eşitleme",
    memoryGraph: "Hafıza haritası",
    sources: "Toplanan veri",
    integrations: "Asistan bağla",
  },
  sidebar: {
    collapse: "Kenar çubuğunu daralt",
    expand: "Kenar çubuğunu genişlet",
    resize: "Kenar çubuğunu boyutlandır",
  },
  commitments: {
    eyebrow: "Şirket hafızası",
    title: "Açık taahhütler",
    /**
     * What the commitments destination says while extraction is off. It has to
     * separate two things a permanently empty list would blur together: "you
     * have no open commitment" and "nobody is looking right now".
     */
    paused: {
      title: "Açık taahhütler bu fazda kapalı",
      body: "Taahhüt çıkarımı maliyet nedeniyle durduruldu, bu yüzden ekran boş bir liste yerine kapalı. Bu, açık taahhüdünüz olmadığı anlamına gelmez; şu anda bakılmıyor. Çıkarım geri geldiğinde ekran da geri gelir.",
    },
    body: "Yetkili kaynaklarda bulunan, açıkça belirtilmiş ve kanıta bağlı taahhütler; durumları ve kaynak alıntılarıyla gösterilir. Bu görünüm salt okunurdur; Obsina sizin adınıza ileti göndermez, işlem yapmaz.",
    search: {
      label: "Açık taahhütlerde ara",
      placeholder: "Tarafa ya da özete göre süz",
      results: (count: number) => `${count} taahhüt eşleşiyor`,
      noResultsTitle: "Aramanızla eşleşen taahhüt yok",
      noResultsBody:
        "Başka bir ad ya da anahtar sözcük deneyin. Aramayı temizlemek yüklenmiş tüm taahhütleri yeniden gösterir.",
    },
    loading: "Açık taahhütler yükleniyor",
    asOf: (time: string) => `${time} itibarıyla durum`,
    listTitle: "Kim neyi borçlu",
    listCount: (count: number) => `${count} taahhüt`,
    /** Used while a next page exists: the loaded count is not the total. */
    listCountPartial: (count: number) =>
      count === 1
        ? "Şimdiye dek 1 taahhüt yüklendi"
        : `Şimdiye dek ${count} taahhüt yüklendi`,
    coverageCompleteTitle: "Bunu yanıtlamak için gereken her şey yerinde",
    coverageCompleteDetail: (count: number) => `${count} kaynak denkleşti`,
    coverageIncompleteTitle:
      "Bazı kaynaklar hâlâ yetişiyor — sonuçlar eksik olabilir",
    /**
     * Heading of a released `partial` answer: the commitments below are real and
     * evidence-bound, and only recall is bounded. See {@link COVERAGE_PARTIAL_LEAD}.
     */
    coveragePartialTitle: COVERAGE_PARTIAL_LEAD,
    /** How far the derived readings behind this answer have come. */
    coveragePartialMedia: (read: number, total: number) =>
      `${total} ekten ${read} tanesi okundu`,
    coveragePartialKnowledge: (read: number, total: number) =>
      `${total} belgeden ${read} tanesi iddialar için okundu`,
    refresh: "Yenile",
    refreshing: "Yenileniyor…",
    loadMore: "Daha fazla taahhüt yükle",
    loadingMore: "Daha fazlası yükleniyor…",
    summaryMissing: "Özet metni yok",
    owner: "Söz veren",
    counterparty: "Karşı taraf",
    counterpartyUnknown: "Belirlenmedi",
    entityReference: (id: string) => `Referans ${id}`,
    entityUnnamed:
      "Bu salt okunur görünümde taraf adları çözülmez; onun yerine kalıcı referans gösterilir.",
    dueOn: (date: string) => `Vade ${date}`,
    dueUnknown: "Kesin vade yok",
    dueNote: "Belirsiz bir vade hiçbir zaman kesin son tarih sayılmaz.",
    statusLabelPrefix: "Durum",
    status: {
      open: "Açık",
      fulfilled: "Yerine getirildi",
      overdue: "Gecikmiş",
      cancelled: "İptal edildi",
      unknown: "Bilinmiyor",
    },
    statusHint: {
      open: "Etkin olarak izleniyor; kesin bir vade geçmedi.",
      fulfilled: "Eşleşen bir giden ileti bu taahhüdü karşılıyor.",
      overdue: "Kesin bir vade, kapsam eksiksizken geçti.",
      cancelled: "Açıkça geri çekildi ya da yerini yenisi aldı; kanıta bağlı.",
      unknown:
        "Karar için kapsam ya da kanıt yetersiz. Uyarı olarak değil, kanıtıyla gösterilir.",
    },
    /**
     * How far the record stands behind the promise itself — a different axis
     * from `status`, which is about what is still owed. The enum names never
     * reach the reader; these are the words that do.
     *
     * `supported` is deliberately wordless. It is the ordinary case, and
     * labelling a plainly stated promise "confirmed" would make the exceptions
     * quieter — the opposite of the point. `null` (an API that stated nothing)
     * is wordless for the same reason it is not `supported`: silence is not a
     * verdict either way.
     */
    trust: {
      /**
       * Said once above the list, not on every row: the reason is common to
       * every mark, and repeating it per row would be the same sentence N times.
       */
      note: "İşaret, kanıtın sözü kesin biçimde belirtmediği anlamına gelir.",
      /** Beside the promise itself, where the doubt belongs. */
      mark: {
        supported: null,
        uncertain: "Doğrulanmadı",
        conflicted: "Kaynaklar çelişiyor",
        superseded: "Yerini yeni kayıt aldı",
      },
      /** The full sentence, in the evidence dialog where there is room for it. */
      detail: {
        supported: null,
        uncertain:
          "Kaynak bu sözü açıkça belirtmek yerine çekinceyle veriyor; bu yüzden olgu değil olasılık olarak tutuluyor.",
        conflicted:
          "Bu sözün arkasındaki kaynaklar birbiriyle uyuşmuyor; bu yüzden hiçbir yanı kesinleşmiş değil.",
        superseded:
          "Daha yeni bir kayıt bunun yerini aldı; kayıt için saklanıyor.",
      },
    },
    viewEvidence: "Kanıtı gör",
    detailsFor: (summary: string) => `Kanıtı aç: ${summary}`,
    evidenceDialogTitle: "Taahhüt kanıtı",
    evidenceDialogBody:
      "Her durum ve taahhüt bir kaynak alıntısına bağlıdır. Burada hiçbir şey gönderilmez, değiştirilmez.",
    statusSection: "Durum",
    evidenceSection: "Kanıt",
    suggestionSection: "Önerilen sonraki adım",
    suggestionBody:
      "Bu salt okunur bir hatırlatmadır. Karşı tarafla kendi posta kutunuzdan iletişime geçmek isteyebilirsiniz. Obsina sizin için ileti göndermez, işlem yapmaz.",
    evidenceExcerpt: "Alıntı",
    evidenceExcerptExpand: "Alıntının tamamını göster",
    evidenceExcerptCollapse: "Daha az göster",
    evidenceSource: "Kaynak",
    evidenceLoading: "Kanıt yükleniyor…",
    evidenceEmpty: "Bu taahhüt için erişilebilir bir kanıt alıntısı yok.",
    evidenceUnavailable: "Kanıt yüklenemedi. Yeniden deneyin.",
    evidenceRetry: "Yeniden dene",
    loadMoreEvidence: "Daha fazla kanıt yükle",
    recordedLabel: "Kaydedildi",
    sourceItemLabel: "Kaynak öğesi",
    emptyTitle: "Açık taahhüt yok",
    emptyBody:
      "Obsina bu hesap için erişilebilir kapsamda açıkça belirtilmiş, kanıta bağlı taahhüt bulamadı.",
    unknownTitle: "Sonuç boş değil, bilinmiyor",
    unknownCoverageBody: unknownCoverageBody("list"),
    unknownEvidenceBody:
      "Erişiminizle bunu yanıtlayacak yetkili kanıt yok. Bu bir uyarı değildir.",
    coverageProgress: (reconciled: string, required: string) =>
      `Gerekli ${required} kaynaktan ${reconciled} tanesi denkleşti`,
    coverageReasonsTitle: "Kapsam boşlukları",
    coverageReasons: {
      required_source_inventory_missing: "Gerekli bir kaynağın envanteri yok.",
      required_source_inventory_incomplete:
        "Gerekli bir kaynak envanteri eksik.",
      source_behind: "Gerekli bir kaynak kapsam sınırının gerisinde.",
      source_failed: "Gerekli bir kaynak eşitlenemedi.",
      source_inaccessible: "Gerekli bir kaynağa erişilemiyor.",
      source_unmapped: "Gerekli bir kaynak eşlenmedi.",
      watermark_missing: "Gerekli bir kaynağın kapsam sınırı yok.",
      watermark_before_as_of: "Kapsam sorgu zamanına ulaşmıyor.",
      snapshot_expired:
        "Okuma anlık görüntüsünün süresi doldu; yenileyip tekrar deneyin.",
      // Not a backlog: the sources are declared but every connection behind
      // them is revoked or inactive. Waiting does nothing; reconnecting does.
      no_active_required_sources:
        "Gerekli bir kaynağın etkin bağlantısı yok — bu boşluğu kapatmak için yeniden bağlayın.",
      // Media and document reasons: each states what has not been read, never
      // that the content is absent.
      media_pending: "Bazı ekler okunuyor.",
      media_partial: "Bazı ekler yalnızca kısmen okunabildi.",
      media_unsupported: "Bazı eklerin biçimini okuyan yok.",
      media_failed: "Bazı ekler okunamadı.",
      media_reading_missing: "Bazı eklerin kayıtlı okuması yok.",
      media_backfill_incomplete: "Ek tarama turu tamamlanmadı.",
      knowledge_pending: "Bazı belgeler iddialar için okunuyor.",
      knowledge_failed: "Bazı belgeler iddialar için okunamadı.",
      knowledge_withdrawn: "Bazı belgelerin iddia okuması geri çekildi.",
      knowledge_extraction_missing: "Bazı belgeler iddialar için okunmadı.",
      mail_folder_scope_limited:
        "Posta kutusunun tamamı değil, seçili klasörleri okundu.",
      mail_folder_scope_member_selected:
        "Posta kutusunun tamamı değil, sizin seçtiğiniz klasörler okundu.",
    },
    unavailableTitle: "Okuma yüzeyi bağlı değil",
    unavailableBody:
      "Taahhüt okuma yüzeyi bu ortamda bağlı değil. Bu tarayıcıya hiçbir sağlayıcı kimlik bilgisi verilmedi.",
    unauthorizedTitle: "Oturum sona erdi",
    unauthorizedBody:
      "Güvenli oturumunuz artık geçerli değil. Taahhütleri görmeden önce yeniden giriş için sayfayı yenileyin.",
    reload: "Yenile",
    errorTitle: "Taahhütler yüklenemedi",
    errorBody: "Obsina açık taahhütleri yükleyemedi. Yeniden deneyin.",
    retry: "Yeniden dene",
  },
  syncProgress: {
    title: "Posta kutusu geçmişi eşitleniyor",
    analyzing: "Seçilen aralık inceleniyor…",
    detailsLabel: "Eşitleme ayrıntılarını aç",
    detailsTitle: "Eşitleme sırasında ne olur?",
    detailsBody:
      "Microsoft kesin bir toplam verebildiğinde Obsina seçilen aralığı sayar, sonra tam metin ve kanıt oluşturmadan önce iletileri ve ekleri envantere alır. Üst sınırlar etiketlenir, hiçbir zaman kesin ilerleme gibi gösterilmez.",
    exactItems: (count: string) => `Seçilen aralıkta ${count} ileti`,
    exactProgress: (processed: string, total: string) =>
      `${total} iletinin ${processed} tanesi envantere alındı`,
    estimatedItems: (count: string) => `En çok ${count} Gelen Kutusu iletisi`,
    // An upper bound is context, not progress: it reads as a quiet second line
    // under the count that is real, and it says that it is an estimate.
    estimateHint: (count: string) =>
      `Tahmin · en çok ${count} Gelen Kutusu iletisi`,
    // The honest wording when the range has no estimate yet. Never replaced by
    // a leftover number from an earlier range.
    estimateUnknown: "Aralık büyüklüğü tahmin edilmedi",
    discoveredItems: (count: string) => `${count} ileti bulundu`,
    incrementalItems: (count: string) =>
      `${count} posta kutusu değişikliği envantere alındı`,
    incrementalVolume: "Son tamamlanan eşitlemeden bu yana değişiklikler",
    incrementalTiming: "Tamamlanma, kalan işleme aşamalarını izler",
    estimateExceeded: (count: string) =>
      `${count} ileti bulundu · tahmin güncelleniyor`,
    estimatingRemaining: "Kalan süre canlı hıza göre tahmin ediliyor",
    // A live estimate derived on the client from how fast the count is moving,
    // never a claimed latency. When throughput is not measurable yet the panel
    // says it is still estimating rather than inventing a number.
    remainingApprox: (duration: string) => `Yaklaşık ${duration} kaldı`,
    durationMinutes: (count: number) => `${count} dakika`,
    durationSeconds: (count: number) => `${count} saniye`,
    selectedRange: "Seçilen aralık",
    expectedVolume: "Beklenen hacim",
    discoveredSoFar: "Şu ana dek bulunan",
    timeEstimate: "Süre tahmini",
    estimatePending: "Seçilen aralık ölçülüyor…",
    stageAnalyze: "Seçilen aralığı incele",
    stageInventory: "İletileri ve ekleri bul",
    stageText: "Okunabilir içeriği çıkar",
    stageEvidence: "Kanıtı oluştur",
    stageVerify: "Kapsamı doğrula",
    stageWaiting: "Bekliyor",
    stageActive: "Sürüyor",
    stageComplete: "Tamamlandı",
  },
  account: {
    menuLabel: "Hesap menüsü",
    signOut: "Çıkış yap",
    signingOut: "Çıkış yapılıyor…",
  },
  syntheticTitle: "Sentetik önizleme",
  syntheticBody:
    "Yalnızca geliştirmeye özgü bu durum müşteri ya da sağlayıcı verisi içermez.",
  // Sync control overview (design reference B, 2026-07-18). The grid is the
  // source surface: working connections/actions stay distinct from planned,
  // explicitly unavailable providers such as Gmail.
  syncControl: {
    eyebrow: "Şirket hafızası",
    title: "Eşitleme",
    pageStatusIntro: "Bağlayıcı sağlığı",
    connectedRegion: "Bağlı hesaplar",
    otherSourcesRegion: "Diğer kaynaklar",
    connectionsHeading: "Kaynaklarınız",
    // The planned cards sit beside the live ones, so this note keeps the
    // boundary explicit: nothing planned is counted as connected.
    plannedNote:
      "Planlanan kaynaklar şeffaflık için listelenir. Bağlı hesap ve hizmet toplamlarınıza sayılmaz.",
    connectionSummary: (accounts: number, services: number | null) => {
      const accountText = `${accounts} hesap`;
      if (services === null) {
        return `${accountText} · bağlı hizmet sayısı bilinmiyor`;
      }
      return `${accountText} · ${services} bağlı hizmet`;
    },
    reconciledUpTo: (time: string) => `${time} tarihine dek denkleşti`,
    // Honest cut-off for coverage that has NOT reconciled: states how far the
    // data reaches without claiming completeness.
    coveredThrough: (time: string) => `${time} tarihine kadarki veri`,
    // No watermark, and the connection has never been attempted: nothing has
    // been read yet, and nothing is in the middle of being read either.
    syncNotStarted: "eşitleme henüz başlamadı",
    // No watermark, but runs have happened. This used to read "hâlâ yetişiyor"
    // — a claim of progress. Measured on 2026-08-08 it was printed over a
    // connection whose runs had all failed for a day, so it told the member the
    // system was working on it when nothing was. An unknown cut-off is stated
    // as unknown; the reason lives in the attention items beside it.
    coverageNotEstablished: "kapsam kesim noktası belirlenmedi",
    // Honest caveat printed beside a reconciled cut-off. The state machine has
    // reconciled the run, but the cumulative coverage can still carry text or
    // evidence items that failed or came through partial. Those never retract
    // "reconciled up to X"; the count sits next to it so the surface never
    // over-claims that everything is readable. Design-expected unsupported items
    // are not counted here. It never claims a retry and never turns the
    // reconciled line back into "still catching up".
    reconciledResidue: (count: number) =>
      `${count} öğe açık bir eksik durumla korunuyor`,
    asOfSeparator: " · ",
    detailsTitle: "Kapsam, geçmiş ve eşitleme yolu",
    detailsBody:
      "Derin denetimleri ve kanıt sayılarını yalnızca gerektiğinde açın.",
    firstRun: {
      title: "İlk kaynağınızı bağlayın",
      body: "Obsina şirket hafızanızı bağladığınız kaynaklardan kurar. Uygun bir posta kutusuyla başlayın, sonra kimin göreceğini ve ne kadar geriye eşitleneceğini seçin.",
      stepConnect: "Bağla",
      stepScope: "Kapsamı ve geçmişi seç",
      stepBuild: "Obsina hafızayı kurar",
    },
    // The one bounded live announcement for the whole surface. Never quantifies
    // anything the viewer cannot see; it counts only the drawn connections.
    liveSummary: (
      accounts: number,
      services: number | null,
      statuses: string,
    ) =>
      accounts === 0
        ? "Henüz bağlı hesap yok."
        : `${accounts} hesap bağlı. ${
            services === null
              ? "Bağlı hizmet sayısı bilinmiyor."
              : `${services} hizmet bağlı.`
          } ${statuses}`,
  },
  connectors: {
    // Provider-parametric chrome: labels are keyed by source kind / provider so
    // future connectors slot in without new hardcoded strings.
    sourceType: {
      mail: "Posta",
      calendar: "Takvim",
      teams: "Teams",
      onedrive: "OneDrive",
      sharepoint: "SharePoint",
      whatsapp: "WhatsApp",
    },
    provider: {
      microsoft: "Microsoft 365",
      google: "Google Workspace",
      whatsapp: "WhatsApp",
    },
    // Compact service-row copy derived from the single Microsoft connection.
    // Rows use only their own authorization and watermark; unknown per-service
    // coverage is stated explicitly and never replaced with a global count.
    sourceCard: {
      notConnectedBody: (sourceType: string) =>
        `Henüz şirket hafızanızın parçası değil. ${sourceType} kaynağını da almak için Microsoft 365'i yeniden bağlayın.`,
      connectCta: (sourceType: string) => `${sourceType} bağla`,
      frozenAt: (time: string) =>
        `Son başarılı eşitlemede donduruldu · ${time}`,
      frozenUnknown: "Donacak başarılı bir eşitleme yok",
      coverageReconciled: (time: string) => `${time} tarihine dek denkleşti`,
      coverageThrough: (time: string) => `${time} tarihine kadar kapsam`,
      // Per-row wording for a service whose own watermark is missing. When every
      // granted service is in that state the rows drop it and the list states it
      // once instead — shorter, never silent.
      coveragePending: "Kapsam belirlenmedi",
      coveragePendingAll:
        "Hiçbir hizmetin kapsam sınırı belirlenmedi. Obsina bunu ilk denkleşen eşitlemeden sonra belirler.",
      connectedCoveragePending: "Bağlı · kapsam bekliyor",
    },
    cardLabel: (sourceType: string, provider?: string) =>
      provider
        ? `${sourceType} bağlayıcısı · ${provider}`
        : `${sourceType} bağlayıcısı`,
    status: {
      healthy: "Güncel",
      syncing: "Eşitleniyor",
      // The self-updating surface never surfaces user-triggering or internal
      // wording, and partial and failed stay distinct in TEXT (not colour
      // alone). Partial is still being worked through, so it reads "Updating".
      // Failed is not claimed to be self-healing — some failed states are
      // terminal and need a resync — so it reads the neutral, honest
      // "Incomplete" without claiming completeness or active retry.
      partial: "Güncelleniyor",
      coverageIncomplete: "Kapsam eksik",
      failed: "Eksik",
      reauth: "Oturum doldu",
      scopePending: "Kurulumu bitir",
      connected: "Bağlandı",
      disconnected: "Bağlı değil",
      connectedWaiting: "İlk eşitleme bekleniyor",
      // A consent was recorded but no device ever linked. Kept distinct from
      // both "Bağlı değil" and "İlk eşitleme bekleniyor" in TEXT: the first
      // would hide that a connection was started, the second would claim a
      // link that does not exist and leave the member with nothing to press.
      linkIncomplete: "Cihaz bağlanmadı",
      configurationRequired: "Kitaplık seçin",
      visibilityRequired: "Özel erişim gerekli",
      planned: "Planlandı",
      // Live inventory progress on the provider pill. Only ever rendered from an
      // exact provider total; an upper bound stays the plain "Syncing" label.
      syncingProgress: (processed: string, total: string) =>
        `Eşitleniyor ${processed}/${total}`,
    },
    lastSuccess: (time: string) => `Son başarılı eşitleme ${time}`,
    lastSuccessNever: "Başarılı eşitleme yok",
    // The demoted provider detail. Everything that used to stack inside the card
    // lives here, one click away, so the card answers "who is connected and what
    // is it doing" at a glance.
    connectionInfo: "Bu bağlantı hakkında",
    connectionDialogTitle: "Bağlantı durumu",
    connectionDialogBody:
      "Bu bağlı hesabın tam durum ayrıntısı. Sayılar ve zaman damgaları yönetim düzleminden gelir; burada ileti içeriği gösterilmez.",
    connectionStateLabel: "Güncel durum",
    connectionServicesLabel: "Hizmetler",
    connectionLastSuccessLabel: "Son başarılı eşitleme",
    connectionCheckedLabel: "Durum en son denetlendi",
    // The per-service explanation that replaced an always-open warning panel.
    sourceStatusInfo: (sourceType: string) => `${sourceType} durumu hakkında`,
    sourceStatusDialogTitle: (sourceType: string) => `${sourceType} durumu`,
    scopePendingMessage:
      "Kurulumu bitirmek için bu kaynağı kimin görebileceğini ve ne kadar geriye eşitleneceğini seçin.",
    connect: {
      mailTitle: "Posta",
      mailBody: "Gelen ve giden posta — taahhütlerinizin omurgası.",
      gmailTitle: "Gmail",
      gmailBody:
        "Gmail kaynak bağlantıları planlandı, henüz kullanılamıyor. Bağlayıcı hazır olana dek Obsina posta kutusu erişimi istemez.",
      cta: (provider: string) => `${provider} bağla`,
      // WhatsApp connect card (self-service). Rendered only behind the
      // VITE_OBSINA_WHATSAPP_ENABLED gate; the wizard collects KVKK consent.
      whatsappTitle: "WhatsApp",
      whatsappBody:
        "WhatsApp Business iletileri — gelen, giden ve grup sohbetleri.",
      // The unfinished link. The pill already names the state, so this line
      // does not repeat it: it states the consequence (nothing is being read)
      // and names re-consent, because the wizard re-records consent under the
      // current notice — a connection consented under a superseded version is
      // ingested by nothing until that happens.
      whatsappResumeBody:
        "Hiçbir ileti okunmuyor. Güncel rıza metnini onaylayıp cihazı bağlayın.",
      whatsappResumeCta: "Bağlanmayı tamamla",
    },
    visibilityPrivate: "Özel",
    visibilityShared: "Ekip",
    visibilityPending: "Kapsam bekliyor",
    microsoftServices: "Microsoft 365 hizmetleri",
    microsoftServicesList: "Posta · Takvim · Teams · OneDrive · SharePoint",
    servicesConnected: (connected: number, total: number) =>
      `${total} hizmetten ${connected} tanesi bağlı`,
    servicesUnavailable: "Hizmet durumu bilinmiyor",
    updateAccess: "Microsoft 365 erişimini güncelle",
    // Owner switch for an optional service. The accessible label carries the
    // service name so a screen reader announces exactly which service a switch
    // controls; the pending line is polite status, announced while the change
    // is in flight. Mail has no switch — it is the always-on coverage anchor.
    serviceToggle: {
      label: (sourceType: string) => `${sourceType} eşitle`,
      pending: (sourceType: string) =>
        `${sourceType} eşitlemesi güncelleniyor…`,
    },
  },
  sharePointLibraries: {
    manage: "Kitaplıkları yönet",
    cardChecking: "Seçili kitaplıklar denetleniyor…",
    cardUnavailable:
      "Kitaplık seçim durumu bilinmiyor. Yeniden denemek için kitaplık yöneticisini açın.",
    cardNeedsSelection:
      "İzin bağlandı · eşitlemeye başlamak için en az bir belge kitaplığı seçin.",
    cardNeedsPrivate:
      "SharePoint kitaplıkları bu Microsoft 365 bağlantısının Özel olmasını gerektirir.",
    cardConfigured: "Kitaplık seçimi yapılandırıldı",
    cardSelectedCount: (count: number) => `${count} kitaplık seçildi`,
    dialogTitle: "SharePoint belge kitaplıkları",
    dialogDescription:
      "Bu Microsoft hesabının erişebildiği SharePoint sitelerinden Obsina'nın envantere alabileceği kitaplıkları seçin.",
    privateRequiredTitle: "Özel Microsoft 365 bağlantısı gerekli",
    privateRequiredBody:
      "SharePoint kitaplık seçimi Ekip bağlantısı kullanamaz. Bu Microsoft 365 bağlantısını Özel yapın; bu, bağlı veriye ekip genelindeki erişimi kaldırır, ardından kitaplık seçimi sürebilir.",
    makePrivate: "Bağlantıyı Özel yap",
    makingPrivate: "Özel yapılıyor…",
    selectedHeading: "Seçili kitaplıklar",
    selectedLoading: "Seçili kitaplıklar yükleniyor…",
    selectedEmpty:
      "Henüz kitaplık seçilmedi. Bir site arayın, sonra bir ya da daha çok belge kitaplığı seçin.",
    searchHeading: "Site listesini daralt",
    searchLabel: "Site adı",
    searchPlaceholder: "Örneğin Finans ya da Operasyon",
    searchHint:
      "Aşağıdaki listeyi süzer. Erişilebilir tüm siteleri görmek için boş bırakın.",
    searchTooShort: "Aramak için en az 2 karakter girin.",
    search: "Siteleri ara",
    searching: "Aranıyor…",
    showAllSites: "Tüm siteleri göster",
    siteResultsHeading: "Siteler",
    sitesLoading: "Siteler yükleniyor…",
    siteResultCount: (count: number) => `${count} site`,
    // The service states that its page is partial. The count is the page the
    // browser actually holds; no tenant-wide total is claimed or implied.
    sitesTruncated: (count: number) =>
      count === 1
        ? "İlk site gösteriliyor. Listeyi daraltmak için arayın."
        : `İlk ${count} site gösteriliyor. Listeyi daraltmak için arayın.`,
    sitesEmpty: "Bu aramayla eşleşen erişilebilir SharePoint sitesi yok.",
    sitesNone: "Bu Microsoft hesabı hiçbir SharePoint sitesine erişemiyor.",
    sitesUnavailableTitle: "Siteler yüklenemedi",
    sitesUnavailableBody: "Aramayı yeniden deneyin.",
    selectionsUnavailableTitle: "Seçili kitaplıklar yüklenemedi",
    selectionsUnavailableBody:
      "Geçerli yapılandırma doğru kalsın diye seçimleri değiştirmeden önce yeniden deneyin.",
    librariesFor: (site: string) => `${site} içindeki belge kitaplıkları`,
    librariesLoading: "Belge kitaplıkları yükleniyor…",
    librariesEmpty: "Bu sitede erişilebilir belge kitaplığı yok.",
    librariesUnavailableTitle: "Belge kitaplıkları yüklenemedi",
    librariesUnavailableBody: "Bu siteyi yeniden yüklemeyi deneyin.",
    chooseSite: "Belge kitaplıklarını görmek için bir site seçin.",
    selected: "Seçili",
    libraryType: "Belge kitaplığı",
    remove: (library: string) => `${library} kitaplığını kaldır`,
    retry: "Yeniden dene",
    close: "Bitti",
    mutationUnavailableTitle: "Kitaplık seçimi güncellenemedi",
    mutationUnavailableBody:
      "Hiçbir şey değişmedi. Bağlantı kullanılabilir olduğunda seçimi yeniden deneyin.",
    addedAnnouncement: (library: string) => `${library} seçildi.`,
    removedAnnouncement: (library: string) => `${library} kaldırıldı.`,
  },
  mailFolders: {
    manage: "Klasörleri seç",
    // The card line. It states the scope as a number of folders, because that
    // is the thing the member changed and the thing coverage reports on.
    cardChecking: "Seçili klasörler denetleniyor…",
    cardUnavailable:
      "Klasör seçim durumu bilinmiyor. Yeniden denemek için klasör seçiciyi açın.",
    cardDefaultScope: "Gelen Kutusu + Gönderilmiş okunuyor",
    cardSelectedCount: (count: number) =>
      count === 1
        ? "Gelen Kutusu + Gönderilmiş ve seçtiğiniz 1 klasör"
        : `Gelen Kutusu + Gönderilmiş ve seçtiğiniz ${count} klasör`,
    dialogTitle: "Okunacak posta klasörleri",
    // Carries the two facts the member needs before choosing, so no second
    // heading block is needed inside the dialog: what is always read, and that
    // a folder added here is read from now on rather than backwards.
    dialogDescription:
      "Gelen Kutusu ve Gönderilmiş her zaman okunur. Eklediğiniz klasör eklendiği andan itibaren okunur; geçmişi taranmaz.",
    filterLabel: "Klasör ara",
    filterPlaceholder: "Klasör adı",
    listHeading: "Posta kutusu klasörleri",
    loading: "Klasörler yükleniyor…",
    empty: "Bu posta kutusunda başka klasör yok.",
    filterEmpty: "Bu aramayla eşleşen klasör yok.",
    unavailableTitle: "Klasörler yüklenemedi",
    unavailableBody: "Yeniden deneyin.",
    selectionsUnavailableTitle: "Seçili klasörler yüklenemedi",
    selectionsUnavailableBody:
      "Geçerli seçim doğru kalsın diye değiştirmeden önce yeniden deneyin.",
    // The service hit its own walk bound. Stated as what it costs the reader,
    // never as a mailbox total the browser cannot establish.
    truncated: "Klasör listesi eksik olabilir; aramayla daraltın.",
    // The two folders the plan always reads. Not a disabled choice with no
    // explanation: the label says what the state is.
    alwaysRead: "Her zaman okunur",
    itemCount: (count: number, locale: string) =>
      `${count.toLocaleString(locale)} öğe`,
    // A cost the member should see before paying it, carried by a word rather
    // than by colour alone.
    largeFolder: "Büyük klasör",
    selectedCount: (count: number) => `${count} klasör seçildi`,
    retry: "Yeniden dene",
    close: "Bitti",
    mutationUnavailableTitle: "Klasör seçimi güncellenemedi",
    mutationUnavailableBody:
      "Hiçbir şey değişmedi. Bağlantı kullanılabilir olduğunda yeniden deneyin.",
    addedAnnouncement: (folder: string) => `${folder} seçildi.`,
    removedAnnouncement: (folder: string) => `${folder} kaldırıldı.`,
  },
  oneDriveFiles: {
    cardNeedsPrivate:
      "OneDrive dosya eşitlemesi bu Microsoft 365 bağlantısının Özel olmasını gerektirir.",
    makePrivate: "Bağlantıyı Özel yap",
    makingPrivate: "Özel yapılıyor…",
  },
  // Processing transparency: what is queued, in flight and recently done across
  // the pipeline. Live counts read on the service row that owns the work; the
  // stage breakdown lives behind the row list's info control. Counts only,
  // never raw errors; an absent backlog reads as an honest "unknown", never as
  // "no work".
  processing: {
    regionLabel: "İşleme kuyruğu",
    title: "İşleniyor",
    detailLabel: "İşleme hakkında",
    dialogBody:
      "Bu bağlantı için kuyrukta, sürmekte ve yakında tamamlanan işler. Sayılar işçi kuyruğundan gelir; burada ileti içeriği ya da sağlayıcı hata metni gösterilmez.",
    asOf: (time: string) => `${time} itibarıyla`,
    unknown: "İşleme durumu bilinmiyor.",
    idle: "Şu anda kuyrukta ya da sürmekte iş yok.",
    // The third state, and the one that was missing. A dead-lettered job has
    // used up its attempts: the worker will not pick it up again, so calling it
    // "retry needed" or folding it into "nothing queued" both tell the member
    // something untrue. It says what happened and what will not happen next.
    stopped: (count: number) =>
      `${count} iş durdu; kendiliğinden yeniden denenmeyecek.`,
    // Everything the read surface counted for this connection that no row above
    // carries: work it could not attribute to one service, and work for a
    // source this card draws no row for. Stated rather than dropped, so the
    // per-row counts are never read as the whole backlog. The wording stays
    // "not listed above" rather than "unattributed", because the second case is
    // attributed — just not to a service shown here.
    unattributedTitle: "Yukarıda listelenmeyen",
    // Türkçede sayıdan sonra çoğul eki gelmez; tek şablon yeter.
    unattributed: (count: number) =>
      `${count} iş yukarıda listelenen bir hizmete bağlı değil`,
    queued: "Kuyrukta",
    inFlight: "Sürüyor",
    recent: "Son 15 dakikada biten",
    failed: "Durdu",
    stageQueued: (count: number) => `${count} kuyrukta`,
    stageInFlight: (count: number) => `${count} sürüyor`,
    stageFailed: (count: number) => `${count} başarısız`,
    // Human labels for the internal pipeline stages. The queue's job-type names
    // stay a stable contract value; only the label shown here is friendly.
    stageLabel: {
      source_inventory: "Envanter",
      source_graph: "İlişkiler",
      canonical_extraction: "Metin çıkarma",
      media_understanding: "Ek okuma",
      knowledge_extraction: "Bilgi",
      fact_resolution: "Olgular",
      projection_refresh: "Yansımalar",
      coverage_reconciliation: "Denkleşme",
    } as Record<string, string>,
  },
  onboarding: {
    eyebrow: "Microsoft 365 bağlantısı",
    title: "Şirket hafızasını kaynağından kurun.",
    body: "Microsoft 365'i bir kez bağlayın. Obsina bağlı posta kutusundaki iletileri envantere alır, desteklenen içeriğin tam metnini korur ve kanıtı kaynağına bağlı tutar.",
    cta: "Microsoft 365 ile başla",
    privacy:
      "Bu bağlantıdan çıkan kanıt yalnızca size özel kalır. Sağlayıcı kimlik bilgileri sunucuda işlenir, bu tarayıcıda asla.",
  },
  // One decision, one card. Visibility is fixed at owner-private in this step, so
  // it is stated as a fact rather than dressed up as a radio the user cannot
  // change — a choice with one option is a false affordance and costs a keyboard
  // stop. The only real decision left is how far back to import, which is why the
  // history controls carry no second heading: their field labels are the heading.
  scope: {
    title: "Kurulumu bitir",
    privateTitle: "Yalnızca ben",
    privateBody:
      "Bu bağlantıdan çıkan kanıtı ve ona dayanan olguları yalnızca siz sorgulayabilirsiniz.",
    defaultBadge: "Zorunlu",
    // The scope is no longer selected here, so "with selected scope" described a
    // choice that does not exist and stretched the button past any label it
    // needed. The button now names what pressing it starts.
    submit: "Eşitlemeyi başlat",
    noClassification:
      "Obsina sizden tek tek öğeleri sınıflandırmanızı ya da birleştirmenizi istemez.",
  },
  coverage: {
    eyebrow: "Eşitleme kapsamı",
    title: "Sessiz boşluksuz posta kutusu kapsamı.",
    body: "Aşağıdaki sayılar bağlı posta kutusunun tüm zamanlarda kaydedip işlediği envanterdir — seçilen eşitleme penceresi değil. Biçimi okunamayan ya da okunamamış öğeler açık bir durum taşır.",
    // Separates the two numbers users otherwise conflate: the selected window is
    // the lower bound for NEW messages a sync fetches; the inventory counts are
    // the cumulative all-time records already held. Stating both, side by side,
    // stops the all-time total from being read as "everything since {date}".
    selectedWindowLabel: "Seçilen eşitleme penceresi",
    selectedWindowNote:
      "Pencere, bir eşitlemenin yeni iletileri ne kadar geriden çekeceğini belirler. Aşağıdaki envanter sayıları hâlihazırda tutulan tüm zaman kayıtlarıdır; bu pencereyle sınırlı değildir.",
    sources: "Kaynak envanteri",
    sourcesDetail: "Kaydedilen tüm zaman posta iletileri",
    attachments: "Ek envanteri",
    attachmentsDetail: "Çıkarımdan önce kaydedilen ekler",
    text: "Tam metin",
    textDetail: "Kanonik metin ya da açık bir işleme durumu",
    graph: "Kanıt işleme",
    graphDetail: "Grafik işleme sonucu verilmiş kanonik belgeler",
    rowDetails: (label: string) => `${label} ayrıntılarını aç`,
    detailsTitle: (label: string) => `${label} ayrıntıları`,
    detailsBody:
      "Bu görünüm koşum kapsamlı işleme sayılarını ve güvenli veri kategorilerini gösterir. İleti gövdeleri ve müşteri içeriği burada açığa çıkmaz.",
    processedData: "İşlenen veri",
    sourceData:
      "Posta kutusu ileti kayıtları, kalıcı tanımlayıcılar, zaman damgaları ve yaşam döngüsü işaretleri.",
    attachmentData:
      "Dosya, öğe, referans ve satır içi ek türleri gibi ek tanımlayıcıları.",
    textData:
      "Kanonik okunabilir metin ya da açık bir kısmi, okunamadı veya biçimi okunamıyor sonucu.",
    graphData:
      "Koşumun kanonik belge sürümlerine bağlı kanıt grafiği işleme sonuçları.",
    completed: "tamamlandı",
    pending: "bekliyor",
    total: "toplam",
    accounted: "hesaba katıldı",
    recorded: "kaydedildi",
    totalPending: "Bekleyen toplam",
    waiting: "Envanter bekleniyor",
    partial: "kısmi",
    failed: "okunamadı",
    unsupported: "biçimi okunamıyor",
    watermark: "Kapsam şu tarihe dek izlendi",
    reconciled: "Geçmiş şu tarihe dek eksiksiz",
    watermarkMissing: "Belirlenmedi",
    incomplete:
      "Kapsam hâlâ eksik. Yapılandırılan posta kutusu geçmişi ve tüm işleme sayıları denkleşmeden Obsina şirket hafızasını eksiksiz diye sunmaz.",
    complete:
      "Yapılandırılan posta kutusu geçmişi ve işleme sayıları gösterilen kapsam sınırında denkleşiyor.",
    // Shown when the run has reconciled (the authoritative state machine reports
    // healthy) yet the cumulative all-time coverage still holds terminal text or
    // evidence residue that failed or came through partial. The history is
    // reconciled at the watermark, so this never retracts the reconciled claim;
    // it names the residue as an explicit incomplete status so a failed>0
    // coverage never reads as "everything is complete". Design-expected
    // unsupported items are excluded from the count.
    reconciledResidue: (count: number) =>
      `Yapılandırılan posta kutusu geçmişi gösterilen kapsam sınırında denkleşir. ${count} öğe eksiksiz sayılmak yerine açık bir eksik durumla korunuyor.`,
  },
  health: {
    eyebrow: "Microsoft 365 sağlığı",
    pageTitle: "Microsoft 365",
    issuesTitle: "İlgi bekleyen öğeler",
    /**
     * What each attention item the control plane can raise says on screen.
     *
     * The API sends a `title` and a `detail` with every issue, both English and
     * both composed on the server; rendering them printed English into a Turkish
     * product and put the wording behind an API deploy. The screen reads `id`
     * instead, and this map is what answers it. `Record` on purpose: a seventh
     * identifier added to the domain fails to compile here until someone writes
     * the sentence for it, rather than reaching a member untranslated.
     *
     * None of these names a control that no longer exists. The surface re-syncs
     * on its own, so a line may say what was observed and what a person can
     * still do — never "press retry".
     */
    issues: {
      microsoft_sync_not_started: {
        title: "Microsoft 365 eşitlemesi başlamadı",
        detail:
          "Bu bağlantı için henüz bir işleme denemesi kaydedilmedi. Bu kaynaktan şirket hafızasına hiçbir şey alınmadı.",
      },
      microsoft_reauthorization_required: {
        title: "Microsoft 365 erişimi yenilenmeli",
        detail:
          "Eşitlemenin sürebilmesi için hesabı yeniden bağlayın. Obsina etkileşimli bir izni arka planda yenileyemez.",
      },
      microsoft_sync_stalled: {
        title: "Microsoft 365 eşitlemesi ilerlemiyor",
        detail:
          "Son 15 dakikada kalıcı bir ilerleme kaydedilmedi. Aşağıdaki sayılar o andan beri değişmedi.",
      },
      filtered_delta_result_limit_reached: {
        title: "Seçilen geçmiş aralığı fazla geniş",
        detail:
          "Microsoft, süzülmüş posta eşitlemesini 5.000 öğeyle sınırlar. Sessiz boşluk kalmadan sürebilmesi için daha yakın bir başlangıç tarihi seçin.",
      },
      microsoft_sync_failed: {
        title: "Microsoft 365 eşitlemesi başarısız oldu",
        detail:
          "Hiçbir şey kaybolmadı ya da yinelenmedi. Sürerse Microsoft 365 erişimini yenileyin.",
      },
      coverage_not_reconciled: {
        title: "Kapsam tam olarak denkleşmedi",
        detail:
          "Kaynak, ek, tam metin ve kanıt kapsamının tamamı eksiksiz olarak kanıtlanmadı.",
      },
    } satisfies Record<
      ControlPlaneIssueId,
      { readonly title: string; readonly detail: string }
    >,
    syncingTitle: "İlk eşitleme sürüyor",
    syncingBody:
      "Önce yakın tarihli öğeler kullanılabilir olabilir. Yetkili posta kutusu geçmişi, kapsam denkleşene dek arka planda sürer.",
    syncingStartingTitle: "Eşitleme başlıyor",
    syncingStartingBody:
      "Obsina, envanter başlamadan önce seçilen posta kutusu aralığını hazırlıyor.",
    syncingIncrementalTitle: "Posta kutusu değişiklikleri eşitleniyor",
    syncingIncrementalBody:
      "Yeni ve değişmiş posta kutusu öğeleri arka planda envanter, metin çıkarma ve kanıt işlemeden geçmeyi sürdürür.",
    healthyTitle: "Microsoft 365 bağlı ve güncel",
    healthyBody:
      "Son eşitleme tamamlandı. Aşağıdaki kapsam sınırını inceleyin.",
    // Partial and failed stay honest and DISTINCT in text, not colour alone.
    // Partial is still making progress, so it reads "still updating" and states
    // that most items are in place with the rest processing. Failed is NOT
    // claimed to be self-healing — some failed states are terminal and need a
    // resync — so it never says it is updating or retrying. It states only the
    // observation-true guarantee (nothing lost or duplicated) and that some
    // items have not synced yet. Neither claims completeness; the coverage
    // watermark and the attention detail below hold the fail-closed truth.
    partialTitle: "Microsoft 365 hâlâ güncelleniyor",
    partialBody: "Öğelerin çoğu yerinde; kalanı arka planda işleniyor.",
    failedTitle: "Microsoft 365 eşitlemesi eksik",
    failedBody:
      "Hiçbir şey kaybolmadı ya da yinelenmedi. Bazı öğeler eşitlenmedi.",
    reauthTitle: "Microsoft 365 erişimi yenilenmeli",
    reauthBody:
      "Saklanan güvenli imleçten devam etmek için yeniden bağlanın. Obsina etkileşimli bir izni arka planda yenileyemez.",
    refresh: "Durumu yenile",
    checked: (time: string) => `${time} tarihinde denetlendi`,
    notChecked: "Denetlenmedi",
    reconnect: "Microsoft 365'i yeniden bağla",
    lastSuccess: "Son başarılı eşitleme",
    lastAttempt: "Son deneme",
    visibility: "Görünürlük",
    connection: "Bağlantı",
  },
  evidence: {
    title: "Eşitleme yolu",
    source: "Microsoft 365 kaynağı",
    inventory: "Envanter",
    text: "Tam metin",
    evidence: "Kanıt",
    notConnected: "Bağlı değil",
    connected: "Bağlantı kuruldu",
    // Per-stage value while setup is unfinished. The reason every stage is
    // waiting is stated once in the summary, so repeating the full sentence on
    // each stage only added height without adding information.
    waiting: "Bekliyor",
    waitingScope: "Görünürlük seçimi bekleniyor",
    waitingConnection: "Bağlandıktan sonra başlar",
    stepsComplete: (complete: number, total: number) =>
      `${total} adımdan ${complete} tanesi tamamlandı`,
    recorded: "kaydedildi",
    of: "/",
  },
  status: {
    notConnected: "Bağlı değil",
    scopeRequired: "Kapsam gerekli",
    syncing: "Eşitleniyor",
    connected: "Bağlandı",
    // Kept consistent with the connector pill and distinct in text: partial is
    // still being worked through ("Updating"); failed is neutral, non-alarming
    // and not claimed to be self-healing ("Incomplete"). Neither claims
    // completeness.
    partial: "Güncelleniyor",
    failed: "Eksik",
    reconnect: "Yeniden bağla",
  },
  history: {
    eyebrow: "Eşitleme geçmişi",
    title: "Ne kadar geriye alınacağını seçin",
    // One shared bound: mail, calendar and Teams all open their history at
    // this same date. Calendar additionally includes upcoming events — the
    // bound only limits how far back the import reaches.
    body: "Bu bağlı Microsoft hesabındaki her kaynak için en eski öğe tarihini belirleyin. Yaklaşan takvim etkinlikleri her zaman dâhildir.",
    label: "Şu tarihten itibaren al",
    hint: "Yeni öğeler kendiliğinden eşitlenmeyi sürdürür.",
    last30Days: "Son 30 gün",
    last90Days: "Son 90 gün",
    lastYear: "Son bir yıl",
    allHistory: "Erişilebilir tüm geçmiş",
    customDate: "Özel tarih",
    // The zone the picked day is read in, named on the control that reads it.
    // Both of these said UTC while the instant behind them was built as UTC
    // midnight — true then, and a day-wide lie the moment either half moves.
    dateLabel: `En eski tarih (${APP_TIME_ZONE_LABEL})`,
    invalidDate: "1 Ocak 2000 ile bugün arasında bir tarih seçin.",
    limitNote: `Tarihler ${APP_TIME_ZONE_LABEL} 00:00'da başlar. Daha dar bir aralık ilk eşitlemeyi hızlandırır; alınmış öğeleri silmez.`,
    // First-run wording, carrying the select's hint too so the controls are
    // followed by one line of small print instead of two. Nothing has been
    // imported yet, so the "does not delete items already imported" reassurance
    // answers a question nobody has.
    setupNote: `Yeni öğeler kendiliğinden eşitlenir. Tarihler ${APP_TIME_ZONE_LABEL} 00:00'da başlar.`,
    save: "Kaydet ve yeniden eşitle",
    saving: "Eşitleme geçmişi kaydediliyor…",
    saved: "Eşitleme geçmişi kaydedildi. Yeni bir eşitleme kuyruğa alındı.",
    unchanged: "Eşitleme geçmişi zaten kaydedilmişti. Yeni eşitleme gerekmedi.",
    accountSaved: "Bu kullanıcı ve bağlı hesap için kaydedildi.",
    estimateChecking: "Seçilen aralık arka planda denetleniyor…",
    estimateExact: (count: string) => `Bu aralıkta şu anda ${count} ileti var.`,
    estimateUpperBound: (count: string) =>
      `Gelen Kutusu'nda şu anda en çok ${count} ileti var.`,
    estimateUnavailable:
      "Eşitleme öncesi sayım yapılamıyor. Eşitleme yine de sürebilir.",
  },
  errors: {
    missingConfig:
      "Microsoft girişi yapılandırılmamış. VITE_OBSINA_API_BASE_URL değerini ayarlayıp sayfayı yenileyin.",
    unavailableTitle: "Yönetim düzlemi kullanılamıyor",
    unavailableBody:
      "Obsina bağlantı sağlığını yükleyemedi. Bu tarayıcıya hiçbir sağlayıcı kimlik bilgisi verilmedi. Yeniden deneyin.",
    staleTitle: "Bağlantı durumu güncel olmayabilir",
    staleBody:
      "Obsina bağlantı sağlığını yenileyemedi. Son doğrulanan durum görünür kalıyor; ona göre hareket etmeden önce yeniden deneyin.",
    actionFailed:
      "Değişiklik kaydedilmedi. Bağlantıyı denetleyip yeniden deneyin.",
    visibilityTransitionRequiresReset:
      "Bu paylaşılan bağlantı zaten eşitlenmiş şirket hafızası içeriyor. Obsina, güvensiz yeniden sınıflandırmayı önlemek için onu değiştirmeden bıraktı. Microsoft dosyaları eşitlenmeden önce bir yönetici bağlantıyı sıfırlayıp Özel olarak yeniden bağlamalıdır.",
    signOutFailed:
      "Çıkış, uygulama oturumunu güvenle temizleyemedi. Yeniden deneyin.",
  },
  memoryGraph: {
    nav: "Hafıza haritası",
    title: "Hafıza haritası",
    // One heading, one line — and the line has to *fit* on one line, because
    // this row's height is height the map does not get. The eyebrow above the
    // title said "Company memory" directly over "Memory graph" (two stacked
    // headings for one page) and the body ran to two lines. "Read-only" is the
    // whole of the promise the second sentence used to spell out; the
    // authorization clause is stated once on the map's own bottom strip, where
    // the reader is looking at the relations it governs.
    //
    // The map is the communication topology now — who wrote to whom, what hangs
    // off which record — so the line no longer promises the commitment network
    // behind it: claim extraction is off and that layer is not being drawn.
    body: "Kaynaklarınızdaki iletişim ağının salt okunur haritası.",
    loading: "Hafıza haritası kuruluyor",
    search: {
      // Sized to the box it sits in: the longer sentence it replaces was cut
      // off mid-word, which is a worse instruction than a shorter one.
      placeholder: "Kişi, şirket ya da proje bulun",
      label: "Haritayı bir varlığa odakla",
      shortcut: "⌘K",
      clear: "Aramayı temizle",
      noMatch: "Bu haritada eşleşen varlık yok",
    },
    stage: {
      asOf: (time: string) =>
        `${time} itibarıyla harita (son tamamlanan eşitleme)`,
      asOfUnknown: "Harita kapsam sınırı belirlenmedi",
      authorizationNote:
        "Yalnızca görmeye yetkili olduğunuz kanıta dayalı ilişkiler.",
      // Bounded fetch: states a subset of authorized entities. It never
      // quantifies or hints at anything the viewer is not authorized to see.
      // Both claims — that the map is bounded and how many it draws — in the
      // width of one strip cell, so the strip stays the single compact line it
      // is meant to be instead of wrapping into the map's height.
      truncated: (shownCount: number) =>
        `Sınırlı harita · yetkili olduğunuz varlıklardan ${shownCount} tanesi gösteriliyor`,
    },
    scale: {
      complete: (entities: number, connections: number) =>
        `Tüm ağ: ${entities} varlık · ${connections} bağ.`,
      truncated: (entities: number, connections: number) =>
        `Tüm ağ (sınırlı): ${entities}+ varlık · ${connections}+ bağ gösteriliyor — yetkili olduğunuz veri bu haritanın çizdiğinden fazla.`,
      // Partial coverage: the counts are exact for what has been read, so they
      // are stated — but the line may not call the network "full". The bottom
      // strip carries what is missing; this one only stops over-claiming.
      partial: (entities: number, connections: number) =>
        `Şu ana dek okunan ağ: ${entities} varlık · ${connections} bağ.`,
      partialTruncated: (entities: number, connections: number) =>
        `Şu ana dek okunan ağ (sınırlı): ${entities}+ varlık · ${connections}+ bağ gösteriliyor — yetkili olduğunuz veri bu haritanın çizdiğinden fazla.`,
      // Number-free by construction: an unresolved answer has no size, and a
      // "0" here would read as "there is nothing" instead of "we have not read
      // it yet". Each line says why the size is not known, and only the lines
      // whose gap can still move mention waiting. The terminal wording stops at
      // "not on its own" rather than "cannot be read": a revoked connection is
      // terminal but perfectly readable once reauthorized, and the reason list
      // below is where the specific remedy is named.
      unknown: {
        settled: "Ağın büyüklüğü bilinmiyor — sayılacak bir sonuç çıkmadı.",
        transient: "Ağın büyüklüğü bilinmiyor — kaynaklar okunuyor.",
        terminal:
          "Ağın büyüklüğü bilinmiyor — bu boşluk kendiliğinden kapanmayacak.",
        mixed:
          "Ağın büyüklüğü bilinmiyor — bir kısmı okunuyor, kalanı kendiliğinden kapanmayacak.",
        unstated: "Ağın büyüklüğü bilinmiyor — kapsam eksik.",
      },
    },
    zoom: {
      zoomIn: "Yakınlaştır",
      zoomOut: "Uzaklaştır",
      fit: "Ekrana sığdır",
    },
    entityType: {
      person: "Kişi",
      company: "Şirket",
      project: "Proje",
      quote: "Teklif",
      order: "Sipariş",
      invoice: "Fatura",
      contract: "Sözleşme",
      document: "Belge",
      mailbox: "Posta kutusu",
      mail_thread: "Posta dizisi",
      source_item: "Kaynak öğesi",
      meeting: "Toplantı",
    },
    // Read source → target. Deliberately plain verbs: the map is read at a
    // glance, so a label that needs a second reading costs more than the code
    // it replaced. Anything outside this map falls back to the raw vocabulary
    // term rather than drawing an unnamed edge.
    relationType: {
      belongs_to_project: "Parçası",
      quote_for: "Teklifi",
      invoice_for: "Faturası",
      sent_to: "E-posta",
      authored_by: "Yazan",
      reply_to: "Yanıt",
      attached_to: "Eki",
      decided: "Karar",
      committed_to: "Taahhüt",
      payment_due: "Ödeme",
      delivery_delayed: "Teslim gecikti",
      complains_about: "Şikâyet",
      supersedes: "Yerine geçen",
      invited_to: "Davet",
      participated_in: "Katıldı",
    },
    // The same vocabulary as a verb, so the hover card can say the whole thing:
    // "Nordwind GmbH emailed Miray Aksoy". Read source → target. A chip on the
    // line names the kind of link; only a sentence says which end did what.
    relationVerb: {
      belongs_to_project: "şunun parçası:",
      quote_for: "şuna teklif:",
      invoice_for: "şuna fatura:",
      sent_to: "şuna e-posta gönderdi:",
      authored_by: "şunun tarafından yazıldı:",
      reply_to: "şunu yanıtladı:",
      attached_to: "şuna ekli:",
      decided: "şuna karar verdi:",
      committed_to: "şuna söz verdi:",
      payment_due: "şuna ödeme borçlu:",
      delivery_delayed: "şuna teslimi gecikti:",
      complains_about: "şundan şikâyet etti:",
      supersedes: "şunun yerine geçti:",
      invited_to: "şunu davet etti:",
      participated_in: "şuna katıldı:",
    },
    // Never the raw code: a relation this build has not been taught still gets a
    // readable sentence, and the map says only what it can stand behind.
    relationVerbUnknown: "şuna bağlı:",
    relationSentence: (subject: string, verb: string, object: string) =>
      `${subject} ${verb} ${object}`,
    // The card under the map. It answers the three questions a reader has while
    // looking — what is this, what is it linked to, and how do you know — before
    // they have clicked anything.
    peek: {
      evidence: (count: number) =>
        count === 1
          ? "Bunu 1 kaynak destekliyor"
          : `Bunu ${count} kaynak destekliyor`,
      // Not "no evidence": what is absent is a source *this reader may open*,
      // which is a statement about authorization and reading, not about the world.
      evidenceNone: "Bunu destekleyen, açabileceğiniz kaynak yok",
      links: (count: number) =>
        count === 1 ? "Bu haritada 1 bağ" : `Bu haritada ${count} bağ`,
      linksNone: "Bu haritada başka hiçbir şey buna bağlı değil",
      more: (count: number) =>
        count === 1 ? "ve 1 bağ daha" : `ve ${count} bağ daha`,
      asOf: (time: string) => `${time} tarihine kadar okundu`,
      asOfUnknown: "ne kadarının okunduğu bilinmiyor",
      open: "Okumak için tıklayın",
    },
    // How far a stated relation or record can be trusted, in words a reader has
    // never had to learn. The map draws these as pattern and shape; the words
    // are what a screen reader hears and what the pointer brings back, so they
    // may never be the only copy of the answer nor a jargon echo of the code.
    claimState: {
      // `supported` needs no wording: it is the unremarkable case, and labelling
      // every ordinary relation "confirmed" would make the exceptions quieter.
      supported: null,
      uncertain: "Doğrulanmadı",
      conflicted: "Kaynaklar çelişiyor",
      superseded: "Yerini yeni kayıt aldı",
    },
    entityStatus: {
      active: null,
      merged: "Başka kayda birleştirildi",
      tombstoned: "Kayıttan çıkarıldı",
    },
    /**
     * Said on the map's own strip, with no node focused: a disagreement nobody
     * clicks on would otherwise never announce itself.
     */
    conflictNotice: (relations: number) =>
      relations === 1
        ? "Buradaki 1 ilişki, birbiriyle çelişen kaynaklardan çiziliyor."
        : `Buradaki ${relations} ilişki, birbiriyle çelişen kaynaklardan çiziliyor.`,
    evidenceKind: {
      mail: "E-posta",
      contract: "Sözleşme",
      invoice: "Fatura",
      quote: "Teklif",
      order: "Sipariş",
      document: "Belge",
      source: "Kaynak",
    },
    evidenceChannel: {
      mail: "Posta",
      calendar: "Takvim",
      teams: "Teams",
    },
    badge: {
      overdue: (days: number) => `${days} gün gecikti`,
      dueSoon: (date: string) => `Vade ${date}`,
    },
    // The panel answers, in order, the four things a reader actually wants: who
    // or what this is, who it is linked to, what was promised, and where any of
    // it came from. Every label is written for someone who has never heard the
    // words this product uses internally: no "entity", no "evidence excerpt",
    // no "email header line", no counter standing in for a sentence. What the
    // old copy hid behind those terms is still stated — a bounded page still
    // says how much it is hiding, an unconfirmed deadline still says so — it is
    // only said in words that leave no question behind.
    inspector: {
      // "As of" reads as a timestamp on the panel; what it actually bounds is
      // how far the sources behind it have been read.
      asOf: (time: string) => `${time} tarihine kadar okundu`,
      asOfUnknown: "Ne kadarının okunduğu bilinmiyor",

      // Who, by name. "3 connections" was a number the reader could do nothing
      // with — the graph already showed a labelled edge while the panel showed
      // a count, so the panel names them and lets the reader jump to one.
      connectionsSection: "Kimlerle bağlantılı",
      connectionsEmpty: (name: string) =>
        `Bu haritada ${name} ile bağlantılı başka bir şey yok.`,
      // The map is a bounded selection, so a complete-looking list of names
      // must say it may not be the whole list.
      connectionsBounded: "Bu harita sınırlı; daha fazlası olabilir.",
      // Eleven address chips in one flat run gave every name the same weight,
      // so the one this entity really deals with looked exactly like the tenth.
      // Folded, the row answers "who, mostly" — the two names the most evidence
      // stands behind — and the rest open in the grouped list below.
      connectionsLead: "En sık",
      connectionsRest: (count: number) => `${count} bağlantı daha`,
      connectionsCollapse: "Listeyi kapat",
      /** An edge the vocabulary does not name; never drawn as a blank group. */
      connectionUnlabeled: "Bağlı",
      connectionFocus: (name: string) => `${name} öğesini haritada göster`,
      // Two people can hold the same name, and two records of one person can
      // too — Obsina does not merge identities, so both reach this list as
      // separate rows. Printing the name alone made that read as a duplicate
      // row; the address beside it is what makes two records legible as two.
      // It is deliberately NOT a claim that they are or are not the same
      // person: the panel shows what each record was recognised by and says
      // nothing further.
      connectionNamed: (name: string, identifier: string) =>
        `${name}, ${identifier}`,
      // A record whose only name is its own address. Saying so, and moving the
      // address to the same quiet position every other row uses, is what keeps
      // one list from being half names and half raw mailboxes.
      connectionUnnamed: "Ad kaydedilmemiş",
      // Shown only when the record really holds more than one, so a single
      // address is never presented as "the" address by omission.
      connectionIdentifierOfMany: (identifier: string, total: number) =>
        `${identifier} ve ${total - 1} tane daha`,

      // Who promised is the question the old "Open items" left open. When every
      // promise here belongs to one person the heading says so once, instead of
      // repeating an owner line under each row.
      openItemsSection: "Açık sözler",
      openItemsBy: (owner: string) => `${owner} neye söz verdi`,
      // One promiser, one recipient: both halves of "who promised whom" in the
      // heading's own sentence. Three rows repeating the same recipient was the
      // same sentence on three lines, and it cost the height that pushed the
      // evidence below the fold.
      openItemsByTo: (owner: string, recipient: string) =>
        `${owner}, ${recipient} kişisine neye söz verdi`,
      emptyOpenItems: (name: string) => `${name} ile ilgili açık bir söz yok.`,
      openItemDue: (due: string) => `Vade ${due}`,
      // Not "no due date": the system cannot tell an unstated deadline from one
      // it failed to resolve, so it states its own confidence, not the source's.
      openItemDueUnknown: "Doğrulayabildiğimiz vade yok",
      openItemOwner: (owner: string) => `Bu sözü ${owner} verdi`,
      // The other half of "who promised whom". A commitment record often names
      // no counterparty at all, and that is stated rather than left blank — an
      // absent recipient line reads as an oversight, not as an answer.
      openItemCounterparty: (name: string) => `${name} kişisine söz verildi`,
      openItemCounterpartyUnknown: "Alıcı belirtilmemiş",

      evidenceSection: "Nereden geliyor",
      emptyEvidence:
        "Bunu destekleyen, görmeye yetkili olduğunuz bir kanıt yok.",
      // The excerpts arrive one round trip after the rest of the panel, because
      // only the entity's own answer says which excerpts exist. Until they do,
      // the sentence above may not be printed: it is a claim, and this is not
      // yet an answer.
      evidenceReading: "Alıntılar okunuyor…",
      // "20 of 39" was a counter, and it left "so where are the other 19?"
      // unanswered. They are further down the same list, and the line now says
      // that in a sentence instead of leaving the reader to infer it.
      evidenceShown: (shown: number, total: number) =>
        `Kayıttaki ${total} alıntının ilk ${shown} tanesi burada.`,
      // The entity holds more than one record may carry, so the total is a
      // lower bound — stated in words rather than as a bare "+".
      evidenceShownTruncated: (shown: number, total: number) =>
        `Kayıtta en az ${total} alıntı var; ilk ${shown} tanesi burada.`,
      evidenceHeaderLine: "Adres ya da konu satırı",
      // A verbatim "To:"/"Cc:"/"Subject:" run. The reader never has to know the
      // word "header": what these lines are is described, not named.
      evidenceHeaderGroup: (count: number) =>
        `E-posta adresi ve konu satırlarında ${count} kez anıldı`,
      evidenceHeaderGroupRange: (from: string, to: string) =>
        from === to ? from : `${from} – ${to}`,
      evidenceHeaderGroupShow: "Bu satırları göster",
      evidenceHeaderGroupHide: "Bu satırları gizle",
      // Field names as the reader's own mail client writes them, not as the
      // transport spells them: nobody needs to know the word "header".
      evidenceHeaderFieldName: {
        to: "Kime",
        from: "Kimden",
        cc: "Cc",
        bcc: "Bcc",
        replyTo: "Yanıt adresi",
        subject: "Konu",
        date: "Tarih",
        sent: "Gönderim",
      },
      evidenceHeaderMention: (
        date: string,
        field: string,
        others: string | null,
      ) =>
        others === null
          ? `${date} · bir e-postanın ${field} satırında anıldı`
          : `${date} · bir e-postanın ${field} satırında, ${others} ile birlikte anıldı`,
      evidenceHeaderMentionSubject: (date: string, subject: string) =>
        `${date} · konusu “${subject}” olan bir e-postada anıldı`,
      evidenceHeaderOthers: (names: readonly string[], overflow: number) => {
        const tail = overflow > 0 ? [`${overflow} kişi daha`] : [];
        const all = [...names, ...tail];
        if (all.length === 1) return all[0] ?? "";
        return `${all.slice(0, -1).join(", ")} ve ${all.at(-1)}`;
      },
      evidenceDerived: "Taramadan okundu",
      evidenceDerivedHint:
        "Taranmış bir görselden makineyle okundu — her sayıyı aslıyla karşılaştırın.",
      evidenceDateUnknown: "Tarih bilinmiyor",
      openEvidence: "Kanıtı aç",
      close: "Bu paneli kapat",
    },
    narrow: {
      info: "Tam harita daha büyük ekranda görünür — burada kümeler ve varlıkları listelenir.",
      searchPlaceholder: "Kişi, şirket ya da projeye odaklan…",
      clustersTitle: "Kümeler",
      clusterSub: (entities: number, evidence: number) =>
        `${entities} varlık · ${evidence} kanıt`,
      entitySub: (type: string, links: number) => `${type} · ${links} bağ`,
      expand: (name: string) => `${name} kümesini aç`,
      collapse: (name: string) => `${name} kümesini kapat`,
    },
    empty: {
      title: "Çizilecek harita yok",
      body: "Obsina bu kapsam sınırında haritaya yerleştirecek yetkili varlık bulamadı.",
    },
    partial: {
      summary: COVERAGE_PARTIAL_SUMMARY,
      lead: COVERAGE_PARTIAL_LEAD,
      media: (read: number, total: number) =>
        `${total} ekten ${read} tanesi okundu`,
      knowledge: (read: number, total: number) =>
        `${total} belgeden ${read} tanesi iddialar için okundu`,
      detailToggle: "Ayrıntı",
      detailLabel: "Bu haritanın kapsam ayrıntısı",
      // A reason token this build has no sentence for. It states that the bound
      // exists and that this build cannot name it, which is the whole truth —
      // printing the token would state neither to anyone who can read it.
      unnamedGap: "Bu sürümün adlandıramadığı bir kapsam kısıtı var.",
    },
    unknown: {
      title: "Sonuç boş değil, bilinmiyor",
      coverageBody: unknownCoverageBody("map"),
      evidenceBody:
        "Erişiminizle ilişki çizecek yetkili kanıt yok. Bu bir uyarı değildir.",
      coverageProgress: (reconciled: string, required: string) =>
        `Gerekli ${required} kaynaktan ${reconciled} tanesi denkleşti`,
      reasonsTitle: "Kapsam boşlukları",
      reasons: {
        required_source_inventory_missing:
          "Gerekli bir kaynağın envanteri yok.",
        required_source_inventory_incomplete:
          "Gerekli bir kaynak envanteri eksik.",
        source_behind: "Gerekli bir kaynak kapsam sınırının gerisinde.",
        source_failed: "Gerekli bir kaynak eşitlenemedi.",
        source_inaccessible: "Gerekli bir kaynağa erişilemiyor.",
        source_unmapped: "Gerekli bir kaynak eşlenmedi.",
        watermark_missing: "Gerekli bir kaynağın kapsam sınırı yok.",
        watermark_before_as_of: "Kapsam sorgu zamanına ulaşmıyor.",
        snapshot_expired:
          "Okuma anlık görüntüsünün süresi doldu; yenileyip tekrar deneyin.",
        // Not a backlog: the sources are declared but every connection behind
        // them is revoked or inactive. Waiting does nothing; reconnecting does.
        no_active_required_sources:
          "Gerekli bir kaynağın etkin bağlantısı yok — bu boşluğu kapatmak için yeniden bağlayın.",
        // Media and document reasons: each states what has not been read, never
        // that the content is absent.
        media_pending: "Bazı ekler okunuyor.",
        media_partial: "Bazı ekler yalnızca kısmen okunabildi.",
        media_unsupported: "Bazı eklerin biçimini okuyan yok.",
        media_failed: "Bazı ekler okunamadı.",
        media_reading_missing: "Bazı eklerin kayıtlı okuması yok.",
        media_backfill_incomplete: "Ek tarama turu tamamlanmadı.",
        knowledge_pending: "Bazı belgeler iddialar için okunuyor.",
        knowledge_failed: "Bazı belgeler iddialar için okunamadı.",
        knowledge_withdrawn: "Bazı belgelerin iddia okuması geri çekildi.",
        knowledge_extraction_missing: "Bazı belgeler iddialar için okunmadı.",
        // The mail scope is a folder list and it is shorter than the mailbox.
        // Stated as what it costs the reader, not as which folders were walked:
        // the folder names are the worker's plan, and a reader cannot act on
        // them. Measured on the live tenant on 2026-08-06, 57,432 items sat
        // outside everything this answer could reach.
        mail_folder_scope_limited:
          "Posta kutusunun tamamı değil, seçili klasörleri okundu.",
        // The same bound, attributed. A member who picked the folders can act
        // on this one — the chooser is where a missing folder is added — so the
        // sentence says whose choice it was rather than repeating the anonymous
        // form above.
        mail_folder_scope_member_selected:
          "Posta kutusunun tamamı değil, sizin seçtiğiniz klasörler okundu.",
      },
    },
    error: {
      title: "Hafıza haritası yüklenemedi",
      body: "Obsina hafıza haritasını yükleyemedi. Yeniden deneyin.",
      retry: "Yeniden dene",
      // Short by design: it shares the toolbar row with the control that caused
      // it, and a long sentence there squeezes the page heading beside it into
      // a column of single words.
      reloadFailed: "Yenileme başarısız — aşağıdaki harita değişmedi.",
    },
    unavailable: {
      title: "Okuma yüzeyi bağlı değil",
      body: "Bilgi okuma yüzeyi bu ortamda bağlı değil. Bu tarayıcıya hiçbir sağlayıcı kimlik bilgisi verilmedi.",
    },
    unauthorized: {
      title: "Oturum sona erdi",
      body: "Güvenli oturumunuz artık geçerli değil. Hafıza haritasını görmeden önce yeniden giriş için sayfayı yenileyin.",
      reload: "Yenile",
    },
  } satisfies MemoryGraphMessages,
  preview: "Sentetik önizleme denetimleri",
};

export type AppMessages = typeof enMessages;
